class StickyHeader extends HTMLElement {
	connectedCallback() {
		this.header = this.closest(".section-header");
		if (!this.header) return;

		this.stickyType = this.dataset.stickyType || "none";
		this.stickyEnabled = this.stickyType !== "none";
		this.headerIsAlwaysSticky = this.stickyType === "always" || this.stickyType === "reduce-logo-size";
		this.transparencyEnabled = this.dataset.transparentEnabled === "true";
		this.transparentTargetSelector = "[data-transparent-header]";
		this.enterOverlapThreshold = 12;
		this.exitOverlapThreshold = 2;
		this.headerBounds = {};
		this.currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
		this.preventReveal = false;
		this.predictiveSearch = this.querySelector("predictive-search");
		this.transparentTargets = [];
		this.isOverTransparent = null;

		this.updateHeaderGroupOffsets = this.updateHeaderGroupOffsets.bind(this);
		this.refreshHeaderGroupSections = this.refreshHeaderGroupSections.bind(this);
		this.refreshTransparentTargets = this.refreshTransparentTargets.bind(this);
		this.requestTransparencyUpdate = this.requestTransparencyUpdate.bind(this);
		this.initialLayout = this.initialLayout.bind(this);
		this.onScrollHandler = this.onScroll.bind(this);

		this.hideHeaderOnScrollUp = () => {
			this.preventReveal = true;
		};

		this.onHeaderGroupOffsetsUpdate = () => {
			window.requestAnimationFrame(this.updateHeaderGroupOffsets);
		};

		this.headerGroupResizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(this.updateHeaderGroupOffsets) : null;

		this.transparencyResizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(this.requestTransparencyUpdate) : null;

		document.addEventListener("header-group:offsets:update", this.onHeaderGroupOffsetsUpdate);

		document.addEventListener("header-group:layout-ready", this.requestTransparencyUpdate);

		this.onBreakpointChange = () => {
			this.setHeaderHeight();
			this.refreshHeaderGroupSections();
			this.refreshTransparentTargets();
		};

		this.breakpointMediaQuery = window.matchMedia("(max-width: 990px)");
		this.breakpointMediaQuery.addEventListener("change", this.onBreakpointChange);

		this.onShopifySectionChange = () => {
			window.requestAnimationFrame(() => {
				this.refreshHeaderGroupSections();
				this.refreshTransparentTargets();
			});
		};

		document.addEventListener("shopify:section:load", this.onShopifySectionChange);

		document.addEventListener("shopify:section:unload", this.onShopifySectionChange);

		this.onWindowLoad = this.initialLayout;

		window.addEventListener("load", this.onWindowLoad, { once: true });

		if (this.headerIsAlwaysSticky) {
			this.header.classList.add("shopify-section-header-sticky");
		}

		this.addEventListener("preventHeaderReveal", this.hideHeaderOnScrollUp);

		window.addEventListener("scroll", this.onScrollHandler, { passive: true });

		this.setHeaderHeight();
		this.refreshHeaderGroupSections();
		this.refreshTransparentTargets();

		if (this.stickyEnabled) {
			this.createObserver();
		}

		this.initialLayout();
	}

	setHeaderHeight() {
		document.documentElement.style.setProperty("--header-height", `${this.header.offsetHeight}px`);
	}

	initialLayout() {
		window.cancelAnimationFrame(this.initialLayoutFrame);
		window.cancelAnimationFrame(this.initialDetectionFrame);

		this.initialLayoutFrame = window.requestAnimationFrame(() => {
			this.setHeaderHeight();
			this.refreshHeaderGroupSections();
			this.refreshTransparentTargets();

			this.initialDetectionFrame = window.requestAnimationFrame(() => {
				this.updateTransparencyState();
			});
		});
	}

	getTransparentTargets() {
		if (!this.transparencyEnabled) return [];

		return Array.from(document.querySelectorAll(this.transparentTargetSelector)).filter((target) => {
			if (this.header.contains(target)) return false;

			const styles = window.getComputedStyle(target);

			return styles.display !== "none" && styles.visibility !== "hidden";
		});
	}

	updateOverlayMode() {
		const mainContent = document.getElementById("MainContent");
		const shouldOverlay = Boolean(mainContent) && this.transparentTargets.some((target) => {
			return Math.abs(target.getBoundingClientRect().top - mainContent.getBoundingClientRect().top) <= this.exitOverlapThreshold;
		});

		if (shouldOverlay) {
			document.documentElement.dataset.headerOverlay = this.stickyEnabled ? "fixed" : "absolute";
		} else {
			delete document.documentElement.dataset.headerOverlay;
		}
	}

	refreshTransparentTargets() {
		this.transparentTargets = this.getTransparentTargets();
		this.updateOverlayMode();

		this.transparencyResizeObserver?.disconnect();

		if (!this.transparencyEnabled) {
			this.setTransparentState(false);
			return;
		}

		this.transparencyResizeObserver?.observe(this.header);

		this.transparentTargets.forEach((target) => {
			this.transparencyResizeObserver?.observe(target);
		});

		// Sync read so first paint after connect already has the correct state
		this.updateTransparencyState();
	}

	requestTransparencyUpdate() {
		if (!this.transparencyEnabled) {
			this.setTransparentState(false);
			return;
		}

		if (this.transparencyFrame !== undefined) return;

		this.transparencyFrame = window.requestAnimationFrame(() => {
			this.transparencyFrame = undefined;
			this.updateTransparencyState();
		});
	}

	getOverlap(headerRect, targetRect) {
		const horizontalOverlap = Math.min(headerRect.right, targetRect.right) - Math.max(headerRect.left, targetRect.left);

		if (horizontalOverlap <= 0) return 0;

		const verticalOverlap = Math.min(headerRect.bottom, targetRect.bottom) - Math.max(headerRect.top, targetRect.top);

		return Math.max(0, Math.round(verticalOverlap));
	}

	setTransparentState(nextState) {
		if (!this.transparencyEnabled) {
			nextState = false;
		}

		if (nextState === this.isOverTransparent) return;

		this.isOverTransparent = nextState;

		this.header.classList.toggle("is-over-transparent-header", nextState);

		this.header.classList.toggle("scrolled-past-header", !nextState);
	}

	updateTransparencyState() {
		if (!this.transparencyEnabled) {
			this.setTransparentState(false);
			return;
		}

		const headerRect = this.header.getBoundingClientRect();

		if (headerRect.width <= 0 || headerRect.height <= 0) {
			return;
		}

		const maximumOverlap = this.transparentTargets.reduce((maximum, target) => {
			return Math.max(maximum, this.getOverlap(headerRect, target.getBoundingClientRect()));
		}, 0);

		if (this.isOverTransparent === true) {
			if (maximumOverlap <= this.exitOverlapThreshold) {
				this.setTransparentState(false);
			}

			return;
		}

		if (maximumOverlap >= this.enterOverlapThreshold) {
			this.setTransparentState(true);
			return;
		}

		if (this.isOverTransparent === null) {
			this.setTransparentState(false);
		}
	}

	refreshHeaderGroupSections() {
		this.headerGroupSections = Array.from(document.querySelectorAll('[class*="shopify-section-group-header-"]'));

		this.headerGroupResizeObserver?.disconnect();

		this.headerGroupSections.forEach((section) => {
			this.headerGroupResizeObserver?.observe(section);

			const brandNavigation = section.querySelector(".header-brand-navigation");

			if (brandNavigation) {
				this.headerGroupResizeObserver?.observe(brandNavigation);
			}
		});

		this.updateHeaderGroupOffsets();
	}

	getHeaderGroupSectionHeight(section) {
		const brandNavigation = section.querySelector(".header-brand-navigation");

		if (brandNavigation) {
			const styles = window.getComputedStyle(brandNavigation);

			const isVisible = styles.display !== "none" && styles.visibility !== "hidden";

			if (!isVisible) {
				document.documentElement.style.setProperty("--header-brand-navigation-height", "0px");

				return 0;
			}

			const height = brandNavigation.getBoundingClientRect().height;

			document.documentElement.style.setProperty("--header-brand-navigation-height", `${height}px`);

			return height;
		}

		return section.getBoundingClientRect().height;
	}

	updateHeaderGroupOffsets() {
		let accumulatedTop = 0;

		this.headerGroupSections?.forEach((section) => {
			section.style.top = `${accumulatedTop}px`;

			accumulatedTop += this.getHeaderGroupSectionHeight(section);
		});

		document.documentElement.style.setProperty("--header-group-height", `${accumulatedTop}px`);

		document.dispatchEvent(new CustomEvent("header-group:layout-ready", { detail: { height: accumulatedTop } }));

		return accumulatedTop;
	}

	disconnectedCallback() {
		this.removeEventListener("preventHeaderReveal", this.hideHeaderOnScrollUp);

		window.removeEventListener("scroll", this.onScrollHandler);

		window.removeEventListener("load", this.onWindowLoad);

		this.breakpointMediaQuery?.removeEventListener("change", this.onBreakpointChange);

		document.removeEventListener("header-group:offsets:update", this.onHeaderGroupOffsetsUpdate);

		document.removeEventListener("header-group:layout-ready", this.requestTransparencyUpdate);

		document.removeEventListener("shopify:section:load", this.onShopifySectionChange);

		document.removeEventListener("shopify:section:unload", this.onShopifySectionChange);

		this.headerGroupResizeObserver?.disconnect();
		this.transparencyResizeObserver?.disconnect();

		delete document.documentElement.dataset.headerOverlay;

		window.cancelAnimationFrame(this.initialLayoutFrame);

		window.cancelAnimationFrame(this.initialDetectionFrame);

		window.cancelAnimationFrame(this.transparencyFrame);
	}

	createObserver() {
		const observer = new IntersectionObserver((entries, intersectionObserver) => {
			this.headerBounds = entries[0].intersectionRect;

			intersectionObserver.disconnect();
		});

		observer.observe(this.header);
	}

	onScroll() {
		this.requestTransparencyUpdate();

		if (!this.stickyEnabled) return;

		if (this.predictiveSearch?.isOpen) return;

		const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

		if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
			if (this.preventHide) return;

			window.requestAnimationFrame(this.hide.bind(this));
		} else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
			if (!this.preventReveal) {
				window.requestAnimationFrame(this.reveal.bind(this));
			} else {
				window.clearTimeout(this.isScrolling);

				this.isScrolling = window.setTimeout(() => {
					this.preventReveal = false;
				}, 66);

				window.requestAnimationFrame(this.hide.bind(this));
			}
		} else if (scrollTop <= this.headerBounds.top) {
			window.requestAnimationFrame(this.reset.bind(this));
		}

		this.currentScrollTop = scrollTop;
	}

	hide() {
		if (this.headerIsAlwaysSticky) return;

		this.header.classList.add("shopify-section-header-hidden", "shopify-section-header-sticky");

		this.closeMenuDisclosure();
		this.closeSearchModal();
	}

	reveal() {
		if (this.headerIsAlwaysSticky) return;

		this.header.classList.add("shopify-section-header-sticky", "animate");

		this.header.classList.remove("shopify-section-header-hidden");
	}

	reset() {
		if (this.headerIsAlwaysSticky) return;

		this.header.classList.remove("shopify-section-header-hidden", "shopify-section-header-sticky", "animate");
	}

	closeMenuDisclosure() {
		this.disclosures = this.disclosures || this.header.querySelectorAll("header-menu");

		this.disclosures.forEach((disclosure) => disclosure.close());
	}

	closeSearchModal() {
		this.searchModal = this.searchModal || this.header.querySelector("details-modal");

		this.searchModal?.close(false);
	}
}

if (!customElements.get("sticky-header")) {
	customElements.define("sticky-header", StickyHeader);
}
